import { useContext } from "react";
import { ResultsContext } from "src/context/resultsContext/context";
import { handleSorting, getSelectedFilters } from "../utils";
import dayjs from "dayjs";

import cloneDeep from "lodash.clonedeep";
import { Bus, FilterList, ResultsData } from "../types";
import { isEmpty, isWindowPresent } from "src/utils/browserHelpers";
import { useIsAnyFilterSelected } from "./useIsAnyFilterSelected";
import { sendBusUIAction } from "src/analytics/useAnalytics";

const useFilters = () => {
  const { applyFilters, setFilterList, filterList, resultsData, govtBuses } =
    useContext(ResultsContext);

  const { selectedFiltersList } = useIsAnyFilterSelected();
  // const history = useHistory();
  // const location = useLocation();

  /**
   * @abstract Generates the results data based on filter selection
   * @param data bus array
   * @param filterSelection object containing updated filter list
   * @returns Filtered bus array
   */

  const computeFilterResults = (
    data: Bus[],
    filterSelection: Record<string, FilterList[]>
  ) => {
    const selected = getSelectedFilters(filterSelection);
    const selectedFilterKeys = Object.keys(selected);

    return data?.filter((bus) => {
      return selectedFilterKeys.every((key) => {
        if (!selected[key].length) return true;
        if (key === "departure") {
          const departureTime = dayjs(bus.deptTime).format("H.mm");
          return selected[key].some((value: any) => {
            const startInt = parseInt(value.start);
            const endInt = parseInt(value.end);
            const busDeptInt = parseFloat(departureTime);
            const isTrue = startInt <= busDeptInt && endInt >= busDeptInt;
            return isTrue;
          });
        } else if (key === "pickupPoints") {
          return selected[key].some((item) =>
            bus.pickups.includes(item as string)
          );
        } else if (key === "dropOffPoints") {
          return selected[key].some((item) =>
            bus.dropOffs.includes(item as string)
          );
        } else if (key === "amenities") {
          return selected[key].some((item) =>
            bus.meta.amenities.includes(item as string)
          );
        } else if (key === "busOperators") {
          return selected[key].some((item) => item === bus.meta.operatorName);
        } else if (key === "starRating") {
          return selected[key].some(
            (rating) => bus.review?.rating >= (rating as number)
          );
        } else if (key === "busType") {
          if (bus.meta?.busType) {
            // ac or non-ac && seter or sleeper

            return selected[key].some((type) => {
              if (type !== "non_ac") {
                return bus.meta?.busType[type as keyof typeof bus.meta.busType];
              } else {
                return !bus.meta?.busType?.hasAc;
              }
            });
          } else false;
        } else if (key === "seatType") {
          if (bus.meta?.busType) {
            //  seter or sleeper

            return selected[key].every((type) => {
              return bus.meta?.busType[type as keyof typeof bus.meta.busType];
            });
          } else false;
        }

        // default condition
        else return true;
      });
    });
  };

  /**
   * @abstract Process the filter selection and returns new response with filter applied
   * @param filterSelection object having all the filters with updated selected state
   *
   */

  const getFilterResults = (filterSelection: Record<string, FilterList[]>) => {
    const selected = getSelectedFilters(filterSelection);

    const newResultsData = cloneDeep(resultsData);
    const filteredGovtBuses = cloneDeep(govtBuses);

    if (selected.sortBy) {
      newResultsData?.buses?.sort(handleSorting(selected.sortBy[0] as string));
    }

    const filterResults = computeFilterResults(
      newResultsData?.buses,
      filterSelection
    );

    const keys = Object.keys(govtBuses);
    keys.forEach((key) => {
      filteredGovtBuses[key] = computeFilterResults(
        filteredGovtBuses[key].sort(
          handleSorting(selected.sortBy[0] as string)
        ),
        filterSelection
      );
    });
    newResultsData.buses = filterResults;

    return { newResultsData, filteredGovtBuses };
  };

  /**
   * @abstract Function to apply filters
   */

  const handleApplyFilters = (newFilterList: {
    [key: string]: FilterList[];
  }) => {
    const { newResultsData, filteredGovtBuses } =
      getFilterResults(newFilterList);

    applyFilters(
      newResultsData as ResultsData,
      filteredGovtBuses,
      newFilterList
    );
    window.scrollTo({
      top: 50,
      left: 0,
      behavior: "smooth",
    });
    const selected = getSelectedFilters(newFilterList);

    if (isWindowPresent) {
      sessionStorage.setItem("selectedFilters", JSON.stringify(selected));
      sessionStorage.setItem("filterList", JSON.stringify(newFilterList));
    }
    const filtersApplied = !isEmpty(selectedFiltersList(selected))
      ? selectedFiltersList(selected)
      : "NA";

    if (filtersApplied !== "NA")
      sendBusUIAction(
        {
          actionName: "srpFilterApplied",
          hasDeparture: filtersApplied?.departure
            ? filtersApplied?.departure?.toString()
            : "No",
          hasAc: filtersApplied?.busType?.includes("hasAc") ? true : false,
          hasNonAc: filtersApplied?.busType?.includes("non_ac") ? true : false,
          hasSeater: filtersApplied?.seatType?.includes("isSeater")
            ? true
            : false,
          hasSleeper: filtersApplied?.seatType?.includes("isSleeper")
            ? true
            : false,
          hasStarRating: filtersApplied?.starRating
            ? filtersApplied?.starRating?.toString()
            : false,
          hasPickupPoints: filtersApplied?.pickupPoints ? true : false,
          hasDroppingPoints: filtersApplied?.dropOffPoints ? true : false,
          hasAmenities: filtersApplied?.amenities
            ? filtersApplied?.amenities?.toString()
            : false,
          hasOperators: filtersApplied?.busOperators
            ? filtersApplied?.busOperators?.toString()
            : false,
          sourceCity: resultsData?.sc?.fromCityName,
          destinationCity: resultsData?.sc?.toCityName,
          travelDate: resultsData?.sc?.journeyDate,
        },
        "b_srp_pageload"
      );
  };
  /**
   * @abstract: handle any change on filter sheet and filter bottom bar
   * @param type: type of filter
   * @param item: object of change filter type, example: {id: 1, title 'AC', selected: false}
   * @param index: index of item in type object
   * @param updatedFilterList: default it selected the context state of filterList, but can send local for bottom bar
   * @param applyAfterChange: flag to automatically apply filter after selection
   */

  const handleChange = (
    type: string,
    item: FilterList,
    index: number,
    updatedFilterList = filterList
  ) => {
    const newSelectionList = cloneDeep(updatedFilterList);
    const filterType = newSelectionList[type as keyof typeof filterList];
    const updateItem = filterType[index];

    if (type === "sortBy") {
      if (updateItem.selected) updateItem.selected = false;
      else {
        const alreadySelectedIndex = filterType.findIndex(
          (item) => item.selected
        );
        if (alreadySelectedIndex > -1) {
          filterType[alreadySelectedIndex].selected = false;
          filterType[index].selected = true;
        } else {
          updateItem.selected = true;
          filterType[index] = updateItem;
        }
      }
    } else {
      updateItem.selected = !updateItem.selected;
      filterType[index] = updateItem;
    }
    newSelectionList[type as keyof typeof filterList] = filterType;

    return newSelectionList;
  };

  /**
   * @abstract: Funct to clear all filter selection
   */
  const handleClearFilters = () => {
    const clearFilterData = { ...filterList };
    const keys = Object.keys(clearFilterData);
    keys.forEach((key) => {
      if (key !== "sortBy")
        clearFilterData[key as keyof typeof filterList] = clearFilterData[
          key as keyof typeof filterList
        ].map((item) => {
          if (item.selected) item.selected = false;
          return item;
        });
    });
    setFilterList(clearFilterData);

    const { filteredGovtBuses, newResultsData } =
      getFilterResults(clearFilterData);
    applyFilters(
      newResultsData as ResultsData,
      filteredGovtBuses,
      clearFilterData
    );
    sessionStorage.removeItem("selectedFilters");
    sessionStorage.removeItem("filterList");
    window.scrollTo(0, 0);
  };

  return {
    handleChange,
    handleClearFilters,
    getFilterResults,
    handleApplyFilters,
  };
};

export default useFilters;
